import { lazy, Suspense } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Navigate, useRoutes } from "react-router-dom";
import { PrivateRoute } from "./utils/PrivateRoutes";
// layouts
import DashboardLayout from "./layouts/dashboard";
import SimpleLayout from "./layouts/simple";
// pages
import { useCheckAuthToken } from "./hooks/useCheckAuthToken";
import DashboardMain from "./pages/DashboardApp/DashboardMain";
import Login from "./pages/Login";
import Page401 from "./pages/Page401";
import Page404 from "./pages/Page404";
import PaymentPage from "./pages/PaymentPage";
import Redirect from "./pages/Redirect";
import Settings from "./pages/Settings";
import User from "./pages/User";
// import TestKG1 from "./pages/TestKG/TestKG1";
import { useAppSelector } from "./app/hooks";
import { RootState } from "./app/store";
import {
    useCheckTLDomainUser,
    useGetTLDomainUserEmail
} from "./hooks/useCheckTLDomainUser";
import LaunchDarklyProvider from "./LaunchDarklyProvider";
import DealerDetail from "./pages/PerseusPay/Dealers/DealerDetails";
import DealerList from "./pages/PerseusPay/Dealers/DealerList";
import DealerImportPage from "./pages/PerseusPay/DealerImport/DealerImportPage";
import JobDetail from "./pages/PerseusPay/DealerImport/JobDetail";
import OnBoarding from "./pages/PerseusPay/OnBoarding/OnBoarding";
import OnBoardingStatus from "./pages/PerseusPay/OnBoarding/OnBoardingStatus";
import PreOnBoarding from "./pages/PerseusPay/OnBoarding/PreOnBoarding";
import Payment from "./pages/PerseusPay/Payment/Payment";
import PricingModelList from "./pages/PerseusPay/PricingModel/PricingModelList";
import SplitConfigurationMaster from "./pages/PerseusPay/PricingModel/SplitConfigurationMaster";
import DailyDepositReport from "./pages/PerseusPay/Reports/DailyDepositReport";
import DepositsReport from "./pages/PerseusPay/Reports/DepositsReport";
import TaxForms from "./pages/PerseusPay/Reports/TaxForms";
import { SignatureRequest } from "./pages/PerseusPay/Signature";
import ManageSurchargeProfile from "./pages/PerseusPay/Surcharge/Surcharge.ManageProfiles";
import SetupSurchargeProfile from "./pages/PerseusPay/Surcharge/Surcharge.SetupProfile";
import Terminals from "./pages/PerseusPay/Terminals";
import Test1 from "./pages/PerseusPay/Test1";
import SwitchToAdyenGateway from "./pages/SwitchDealerGateway/SwitchToAdyenGateway";
import { TransactionsGrid } from "./pages/TransactionNew/TransactionsGrid";
import ConfigureWNF from "./pages/WNF/ConfigureWebHook";
import ManageWNF from "./pages/WNF/ManageWebHooks";
import ModifyWNF from "./pages/WNF/ModifyWebHook";
import { isEmpty } from "./utils/Util";

// Lazy-loaded so these stay out of the main bundle (user story 171192).
// Payment.tsx lazy-loads the same modules; a static import here would pull
// them (and the Adyen Web SDK) back into main.js and defeat the split.
const AdyenPayments = lazy(
    () => import("./pages/PerseusPay/AdyenPaymentDropIn")
);
const AdyenTransactions = lazy(
    () => import("./pages/PerseusPay/AdyenTransactions")
);
// Public ACH enrollment page. Lazy for the same reason: it will host the Adyen ACH component.
const AchEnrollment = lazy(
    () => import("./pages/PerseusPay/ACH/Enrollment/AchEnrollment")
);
// The customer's signed authorization PDF, opened in a new tab from the enrollment page (182148).
const SignedConsent = lazy(
    () => import("./pages/PerseusPay/ACH/Enrollment/SignedConsent")
);
const ConsentNotAvailable = lazy(
    () => import("./pages/PerseusPay/ACH/Enrollment/ConsentNotAvailable")
);

const LazyFallback = () => (
    <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ padding: "20px" }}
    >
        <CircularProgress size="1.5rem" />
    </Box>
);

// ----------------------------------------------------------------------

export default function Router() {
    const isTLDomainUser = useCheckTLDomainUser(); // Get the value of isTLDomainUser
    const isAuthenticated = useCheckAuthToken(); // Get the value of isAuthenticated
    const userEmail = useGetTLDomainUserEmail();
    const dealerId = useAppSelector((state: RootState) => state.dealer.dealerId); // Get DealerId from state

    const routes = useRoutes([
        // {
        //   element: <PrivateRoute
        //     element={<Adyen />}
        //     isAuthenticated={useCheckAuthToken}
        //   />, path: "/adyen"
        // },
        // {
        //   path: "/adyen", children: [
        //     {
        //       path: "transaction",
        //       element: <PrivateRoute
        //         element={<Adyen />}
        //         isAuthenticated={useCheckAuthToken}

        //       />
        //     }

        //   ]
        // },
        {
            path: "test",
            element: <Test1 />
        },
        {
            path: "/dashboard",
            element: <DashboardLayout />,
            children: [
                { element: <Navigate to="/dashboard/app" />, index: true },

                {
                    path: "app",
                    element: !isTLDomainUser ? (
                        <PrivateRoute
                            // element={<DashboardApp />}
                            // isAuthenticated={useCheckAuthToken}
                            element={<DashboardMain />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },

                {
                    path: "transactions",
                    element: !isTLDomainUser ? (
                        <PrivateRoute
                            element={<TransactionsGrid />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "users",
                    element: !isTLDomainUser ? (
                        <PrivateRoute
                            element={<User />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "settings/:dealerId",
                    element: !isTLDomainUser ? (
                        <PrivateRoute
                            element={<Settings />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "perseuspaysetting",
                    element: !isTLDomainUser ? (
                        <PrivateRoute
                            element={<OnBoarding />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "terminalsetting",
                    element: !isTLDomainUser ? (
                        <PrivateRoute
                            // element={<TerminalSettings />}
                            element={<Terminals />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "dailyDepositReport",
                    element: !isTLDomainUser ? (
                        <PrivateRoute
                            element={<DailyDepositReport />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "depositsReport",
                    element: !isTLDomainUser ? (
                        <PrivateRoute
                            element={<DepositsReport />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "taxForms",
                    element: !isTLDomainUser ? (
                        <PrivateRoute
                            element={<TaxForms />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },

                {
                    path: "preonboarding",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<PreOnBoarding />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },

                {
                    path: "onboardingStatus",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<OnBoardingStatus />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },

                {
                    path: "SwitchToAdyenGateway",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<SwitchToAdyenGateway />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "dealers",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<DealerList />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "dealerDetail",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<DealerDetail />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "dealer-import",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<DealerImportPage />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "dealer-import/:jobId",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<JobDetail />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "surchargeSetup",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<SetupSurchargeProfile />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "manageProfiles",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<ManageSurchargeProfile />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "adyen-payments",
                    element: !isTLDomainUser ? (
                        <PrivateRoute
                            element={
                                <Suspense fallback={<LazyFallback />}>
                                    <AdyenPayments
                                        token={""}
                                        onPaymentStarted={null}
                                        onPaymentCompleted={null}
                                        saveCard={false}
                                    />
                                </Suspense>
                            }
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "configureWebhook",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<ConfigureWNF />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "manageWebHooks",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<ManageWNF />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "modifyWebHook",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<ModifyWNF />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "pricingmodel",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<PricingModelList />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "pricingmodelsetup",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<SplitConfigurationMaster />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                },
                {
                    path: "pricingmodelsetup/:id/:splitconfigid",
                    element: isTLDomainUser ? (
                        <PrivateRoute
                            element={<SplitConfigurationMaster />}
                            isAuthenticated={isAuthenticated}
                        />
                    ) : (
                        <Navigate to="/login" />
                    )
                }

                // {
                //   path: "vaulted-card",
                //   element: (
                //     <PrivateRoute
                //       element={<VaultedCard />}
                //       isAuthenticated={useCheckAuthToken}
                //     />
                //   ),
                // },
            ]
        },
        // ---

        // ---
        {
            path: "/adyentransactions",
            element: !isTLDomainUser ? (
                <Suspense fallback={<LazyFallback />}>
                    <AdyenTransactions />
                </Suspense>
            ) : (
                <Navigate to="/login" />
            )
        },
        {
            path: "/onboarding",
            element: !isTLDomainUser ? <OnBoarding /> : <Navigate to="/login" />
        },

        {
            path: "/payment",
            element: !isTLDomainUser ? <Payment /> : <Navigate to="/login" />
        },
        {
            path: "/RequestSignature",
            element: !isTLDomainUser ? <SignatureRequest /> : <Navigate to="/login" />
        },
        {
            path: "/savecard",
            element: !isTLDomainUser ? <Payment /> : <Navigate to="/login" />
        },
        {
            // Anonymous customer page: the token in the URL is the only credential. Unlike the routes
            // above it is not gated on isTLDomainUser — it uses no portal session, so it must work the
            // same for whoever opens the link, including an internal user testing it.
            path: "/ACH/Enroll/:token",
            element: (
                <Suspense fallback={<LazyFallback />}>
                    <AchEnrollment />
                </Suspense>
            )
        },
        {
            // Public, like the enrollment page: the new tab "Download PDF Consent" opens (182148).
            path: "/ACH/Enroll/:token/Consent",
            element: (
                <Suspense fallback={<LazyFallback />}>
                    <SignedConsent />
                </Suspense>
            )
        },
        {
            // Where the API sends a signed-PDF tab it will not serve. A fixed segment, so it is matched before
            // "/ACH/Enroll/:token".
            path: "/ACH/Enroll/ConsentNotAvailable",
            element: (
                <Suspense fallback={<LazyFallback />}>
                    <ConsentNotAvailable />
                </Suspense>
            )
        },

        {
            path: "settings/:dealerId",
            element: !isTLDomainUser ? <Settings /> : <Navigate to="/login" />
        },

        {
            element: <SimpleLayout />,
            children: [
                { element: <Navigate to="/dashboard/app" />, index: true },
                // {
                //   path: "vaulted-card",
                //   element: <VaultedCard />,
                // },
                // {
                //   path: "settings/:dealerId",
                //   element: <Settings />,
                // },
                { path: "*", element: <Navigate to="/404" /> },
                { path: "*", element: <Navigate to="/401" /> }
            ]
        },

        {
            path: "payment-page",
            element: !isTLDomainUser ? <PaymentPage /> : <Navigate to="/login" />
        },

        {
            path: "redirect",
            element: <Redirect />
        },

        {
            path: "login",
            element: <Login />
        },

        {
            path: "401",
            element: <Page401 />
        },

        {
            path: "404",
            element: <Page404 />
        },

        {
            path: "*",
            element: <Navigate to="/404" replace />
        }
    ]);

    // Wrap all routes in LaunchDarklyProvider if dealerId is available
    if (!isTLDomainUser && !isEmpty(dealerId)) {
        return (
            <LaunchDarklyProvider dealerId={dealerId} userEmail={undefined}>
                {routes}
            </LaunchDarklyProvider>
        );
    } else if (isTLDomainUser && !isEmpty(userEmail)) {
        return (
            <LaunchDarklyProvider dealerId={undefined} userEmail={userEmail}>
                {routes}
            </LaunchDarklyProvider>
        );
    } else {
        // Fallback when dealerId is not yet available (e.g. on login)
        return routes;
    }
}
